import { useCallback, useState } from 'react';

export type DragReorderProps = {
  draggable: boolean;
  onDragStart?: React.DragEventHandler<HTMLDivElement>;
  onDragOver?: React.DragEventHandler<HTMLDivElement>;
  onDragLeave?: React.DragEventHandler<HTMLDivElement>;
  onDrop?: React.DragEventHandler<HTMLDivElement>;
  onDragEnd?: React.DragEventHandler<HTMLDivElement>;
  isDragOver: boolean;
  isDragging: boolean;
};

export const useDragReorder = <T>(
  items: T[],
  onChange: (items: T[]) => void
) => {
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [dragTargetIndex, setDragTargetIndex] = useState<number | null>(null);

  const reorder = useCallback(
    (source: number, destination: number) => {
      if (source === destination) return;
      const nextItems = [...items];
      const [movedItem] = nextItems.splice(source, 1);
      nextItems.splice(destination, 0, movedItem);
      onChange(nextItems);
    },
    [items, onChange]
  );

  const handleDragStart = useCallback(
    (index: number): React.DragEventHandler<HTMLDivElement> =>
      event => {
        setDraggingIndex(index);
        event.currentTarget.style.cursor = 'grabbing';
        document.body.style.cursor = 'grabbing';
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', String(index));
      },
    []
  );

  const handleDragOver = useCallback(
    (index: number): React.DragEventHandler<HTMLDivElement> =>
      event => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        setDragTargetIndex(index);
      },
    []
  );

  const handleDragLeave = useCallback(
    (index: number): React.DragEventHandler<HTMLDivElement> =>
      () => {
        if (dragTargetIndex === index) {
          setDragTargetIndex(null);
        }
      },
    [dragTargetIndex]
  );

  const handleDrop = useCallback(
    (index: number): React.DragEventHandler<HTMLDivElement> =>
      event => {
        event.preventDefault();
        const source = Number(event.dataTransfer.getData('text/plain'));
        if (!Number.isNaN(source) && source !== index) {
          reorder(source, index);
        }
        setDraggingIndex(null);
        setDragTargetIndex(null);
      },
    [reorder]
  );

  const handleDragEnd = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.currentTarget.style.cursor = '';
      document.body.style.cursor = '';
      setDraggingIndex(null);
      setDragTargetIndex(null);
    },
    []
  );

  const getDragProps = useCallback(
    (index: number, enabled = true): DragReorderProps => ({
      draggable: enabled,
      onDragStart: enabled ? handleDragStart(index) : undefined,
      onDragOver: enabled ? handleDragOver(index) : undefined,
      onDragLeave: enabled ? handleDragLeave(index) : undefined,
      onDrop: enabled ? handleDrop(index) : undefined,
      onDragEnd: enabled ? handleDragEnd : undefined,
      isDragOver: enabled && dragTargetIndex === index,
      isDragging: enabled && draggingIndex === index,
    }),
    [
      dragTargetIndex,
      draggingIndex,
      handleDragEnd,
      handleDragLeave,
      handleDragOver,
      handleDrop,
      handleDragStart,
    ]
  );

  return { getDragProps, draggingIndex, dragTargetIndex };
};
